import React, { useState } from "react";
import { Button, Form, Input, notification } from "antd";
import PaxCounterCard from "./PaxCounterCard";
import SelectableOptionCard from "./SelectableOptionCard";

const ICONS = "/iceholidays-assets/images/icons";

const INTEREST_OPTIONS = [
  { value: "culture_heritage", label: "Culture & heritage", icon: `${ICONS}/icon-interest-culture.png` },
  { value: "food_local_markets", label: "Food & local markets", icon: `${ICONS}/icon-interest-food.png` },
  { value: "nature_scenery", label: "Nature & scenery", icon: `${ICONS}/icon-interest-nature.png` },
  { value: "beach_relaxation", label: "Beach & relaxation", icon: `${ICONS}/icon-interest-beach.png` },
  { value: "luxury_wellness", label: "Luxury & wellness", icon: `${ICONS}/icon-interest-luxury.png` },
  { value: "art_museums_galleries", label: "Art, museums & galleries", icon: `${ICONS}/icon-interest-art.png` },
  { value: "adventure_outdoor", label: "Adventure & outdoor activities", icon: `${ICONS}/icon-interest-adventure.png` },
  { value: "shopping_city", label: "Shopping & city exploration", icon: `${ICONS}/icon-interest-shopping.png` },
];

const CUSTOMIZATION_OPTIONS = [
  { value: "extend_travel_period", label: "Extend the travel period" },
  { value: "add_extra_destinations", label: "Add extra destinations or stopovers" },
  { value: "upgrade_hotel", label: "Upgrade hotel accommodation" },
];

const PAX_TYPES = [
  { key: "adult", label: "Adult", subtext: "12 years & above" },
  { key: "children", label: "Children", subtext: "2-11 years old" },
  { key: "infant", label: "Infant", subtext: "Under 2 years old" },
] as const;

type PaxKey = (typeof PAX_TYPES)[number]["key"];

export interface TripPreferenceValues {
  adult: number;
  children: number;
  infant: number;
  interests: string[];
  customizations: string[];
  customizationOther: string;
}

interface Props {
  initialValues: TripPreferenceValues | null;
  onBack: () => void;
  onNext: (values: TripPreferenceValues) => void;
}

export default function TripPreferenceStep({ initialValues, onBack, onNext }: Props) {
  const [form] = Form.useForm();
  const [counts, setCounts] = useState<Record<PaxKey, number>>({
    adult: initialValues?.adult ?? 1,
    children: initialValues?.children ?? 0,
    infant: initialValues?.infant ?? 0,
  });
  const [interests, setInterests] = useState<string[]>(initialValues?.interests || []);
  const [customizations, setCustomizations] = useState<string[]>(initialValues?.customizations || []);

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const setCount = (key: PaxKey, delta: number) =>
    setCounts((prev) => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));

  const handleFinish = (values: { customizationOther?: string }) => {
    if (customizations.length === 0 && !values.customizationOther?.trim()) {
      notification.warning({ message: "Please select how you'd like to customize the tour." });
      return;
    }
    onNext({
      ...counts,
      interests,
      customizations,
      customizationOther: values.customizationOther?.trim() || "",
    });
  };

  return (
    <div className="customize-tour-form__step-body">
      <Form
        form={form}
        layout="vertical"
        initialValues={{ customizationOther: initialValues?.customizationOther }}
        onFinish={handleFinish}
      >
        <div className="customize-tour-form__section">
          <h3 className="customize-tour-form__label">How many traveller you are planning for?*</h3>
          <div className="customize-tour-form__pax-grid">
            {PAX_TYPES.map(({ key, label, subtext }) => (
              <PaxCounterCard
                key={key}
                label={label}
                subtext={subtext}
                value={counts[key]}
                onDecrement={() => setCount(key, -1)}
                onIncrement={() => setCount(key, 1)}
              />
            ))}
          </div>
        </div>

        <div className="customize-tour-form__section">
          <h3 className="customize-tour-form__label">What experiences interest you most?</h3>
          <div className="customize-tour-form__option-grid customize-tour-form__option-grid--interests">
            {INTEREST_OPTIONS.map((opt) => (
              <SelectableOptionCard
                key={opt.value}
                icon={opt.icon}
                label={opt.label}
                selected={interests.includes(opt.value)}
                onClick={() => setInterests((prev) => toggle(prev, opt.value))}
              />
            ))}
          </div>
        </div>

        <div className="customize-tour-form__section">
          <h3 className="customize-tour-form__label">How would you like to customize the tour?*</h3>
          <div className="customize-tour-form__option-grid customize-tour-form__option-grid--customization">
            {CUSTOMIZATION_OPTIONS.map((opt) => (
              <SelectableOptionCard
                key={opt.value}
                label={opt.label}
                selected={customizations.includes(opt.value)}
                onClick={() => setCustomizations((prev) => toggle(prev, opt.value))}
              />
            ))}
          </div>
          <Form.Item name="customizationOther" className="customize-tour-form__other-input">
            <Input.TextArea rows={4} placeholder="Or something else that is not listed" />
          </Form.Item>
        </div>

        <div className="customize-tour-form__actions">
          <Button className="customize-tour-form__btn-back" onClick={onBack}>
            Back
          </Button>
          <Button type="primary" htmlType="submit" className="customize-tour-form__btn-continue">
            Continue
          </Button>
        </div>
      </Form>
    </div>
  );
}
